import Service from 'react-reservice';

import { ITradingAccount } from '@webtrader/core/api/user';
import { IAuthUserDetails } from '@webtrader/core/api/user/login';
import { kSeparatorForNumberOrDecimals } from '@webtrader/core/format/price';
import mixpanel from '@webtrader/core/mix-panel';
import { EMixpanelEventNames, EMixpanelProperties } from '@webtrader/core/services/analysis/constants';
import superPropertiesService from '@webtrader/core/services/analysis/superProperties/superPropertiesService';
import { authService } from '@webtrader/core/services/auth';
import { companyService } from '@webtrader/core/services/company';

import { IMixpanelUserDetails } from './type';

class UserAnalysisService extends Service {
  private getTenantIdFromTraderSubscription = (traderSubscription: string) => {
    const traderSubscriptionArray = traderSubscription.split('|');
    return traderSubscriptionArray[0] ?? '-';
  };

  public identifyUser = (tradingAccount: ITradingAccount) => {
    const { mainProductCodeList = [] } = companyService.context.company || {};
    const {
      metatraderId,
      accountName,
      leverage,
      marketSubscription,
      platform,
      traderSubscription,
      userGroup,
      baseCurrency,
      balance,
      userId,
      equity,
      marginLevel,
      isDemo
    } = tradingAccount;

    mixpanel.identify(metatraderId.toString());

    const tenantId = undefined;

    const { source, loginAs } = authService.context;

    superPropertiesService.registerSuperProperties({
      [EMixpanelProperties.SOURCE]: source,
      [EMixpanelProperties.TENANT_ID]: tenantId || this.getTenantIdFromTraderSubscription(traderSubscription),
      [EMixpanelProperties.USER_ACCOUNT_TYPE]: loginAs,
      [EMixpanelProperties.IS_DEMO]: isDemo,
      [EMixpanelProperties.MAIN_PRODUCT_CODE]: mainProductCodeList[0] ?? '',
      [EMixpanelProperties.METATRADER_GROUP]: userGroup
    });

    this.setUserProfile({
      [EMixpanelProperties.ACCOUNT_BALANCE]: balance,
      [EMixpanelProperties.ACCOUNT_NAME]: accountName,
      [EMixpanelProperties.BASE_CURRENCY]: baseCurrency,
      [EMixpanelProperties.LEVERAGE]: leverage ? kSeparatorForNumberOrDecimals(leverage) : '-',
      [EMixpanelProperties.MARKET_SUBSCRIPTION]: marketSubscription,
      [EMixpanelProperties.NAME]: accountName,
      [EMixpanelProperties.PLATFORM]: platform,
      [EMixpanelProperties.TENANT_ID]: tenantId ?? this.getTenantIdFromTraderSubscription(traderSubscription),
      [EMixpanelProperties.TRADER_SUBSCRIPTION]: traderSubscription,
      [EMixpanelProperties.USER_GROUP]: userGroup,
      [EMixpanelProperties.USER_ID]: userId,
      [EMixpanelProperties.EQUITY]: equity,
      [EMixpanelProperties.MARGIN_LEVEL]: marginLevel,
      [EMixpanelProperties.IS_DEMO]: isDemo,
      [EMixpanelProperties.METATRADER_GROUP]: userGroup,
      [EMixpanelProperties.MAIN_PRODUCT_CODE]: mainProductCodeList[0] ?? ''
    });
  };

  public accountSwitchClicked = () => {
    mixpanel.track(EMixpanelEventNames.ACCOUNT_SWITCH_CLICKED);
  };

  public accountSwitchConfirmClicked = () => {
    mixpanel.track(EMixpanelEventNames.ACCOUNT_SWITCH_CONFIRM_CLICKED);
  };

  public logout = (switchAccount = false) => {
    mixpanel.track(EMixpanelEventNames.ACCOUNT_LOGOUT, {
      [EMixpanelProperties.IS_SWITCH_ACCOUNT]: switchAccount
    });
  };

  public login = (user: IAuthUserDetails) => {
    const { tenantId } = companyService.context.company || {};
    const { loginAs } = authService.context || {};
    mixpanel.identify(user?.metatraderId?.toString());
    mixpanel.track(EMixpanelEventNames.ACCOUNT_LOGIN, {
      [EMixpanelProperties.PAGE]: '-',
      [EMixpanelProperties.TENANT_ID]: tenantId ?? '-',
      [EMixpanelProperties.SOURCE]: user.source,
      [EMixpanelProperties.METATRADER_ID]: user.metatraderId,
      [EMixpanelProperties.METATRADER_GROUP]: user.metatraderGroup,
      [EMixpanelProperties.IS_DEMO]: user.isDemo,
      [EMixpanelProperties.TRADER_SUBSCRIPTION]: user.traderSubscription,
      [EMixpanelProperties.MARKET_SUBSCRIPTION]: user.marketSubscription,
      [EMixpanelProperties.MAIN_PRODUCT_CODE]: user.mainProductCode,
      [EMixpanelProperties.USER_ACCOUNT_TYPE]: loginAs
    });
  };

  public switchAccount = (tradingAccount: ITradingAccount) => {
    const { loginAs } = authService.context;
    this.logout(true);
    this.identifyUser(tradingAccount);

    mixpanel.track(EMixpanelEventNames.ACCOUNT_SWITCHED, {
      [EMixpanelProperties.PAGE]: '-',
      [EMixpanelProperties.METATRADER_ID]: tradingAccount?.metatraderId,
      [EMixpanelProperties.IS_DEMO]: tradingAccount?.isDemo,
      [EMixpanelProperties.TRADER_SUBSCRIPTION]: tradingAccount?.traderSubscription,
      [EMixpanelProperties.MARKET_SUBSCRIPTION]: tradingAccount?.marketSubscription,
      [EMixpanelProperties.BASE_CURRENCY]: tradingAccount?.baseCurrency,
      [EMixpanelProperties.USER_ACCOUNT_TYPE]: loginAs
    });
  };

  private setUserProfile = (propeties: IMixpanelUserDetails) => {
    mixpanel.setUserProfile({ ...propeties });
  };
}

const userAnalysisService = new UserAnalysisService();
export default userAnalysisService;
